// apps/web/src/components/estoque/celula-select.tsx — Fase 13, Plano 11 (EST-16, D-07).
// Select das células (setores) ATIVAS, sobre GET /api/v1/estoque/lookups/celulas (estoque:consultar; até 20 por consulta).
// Quando a consulta atinge o limite, aparece uma busca por código/nome para alcançar as demais células.
import { useEffect, useRef, useState } from 'react';
import type { CelulaLookup } from '@pieltec/contracts';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

export interface CelulaSelectProps {
  /** id da célula escolhida; '' = nenhuma. */
  readonly value: string;
  readonly onChange: (id: string) => void;
  /** Código da opção já selecionada, usado para buscá-la mesmo fora da primeira página. */
  readonly codigoSelecionado?: string | null;
  readonly id?: string;
  readonly desabilitada?: boolean;
  readonly invalido?: boolean;
  readonly className?: string;
}

const LIMITE_LOOKUP = 20;
const ATRASO_BUSCA_MS = 250;

function rotulo(celula: CelulaLookup): string {
  return `${celula.codigo} — ${celula.nome}`;
}

export function CelulaSelect({ value, onChange, codigoSelecionado = null, id, desabilitada = false, invalido = false, className }: CelulaSelectProps) {
  const [celulas, setCelulas] = useState<readonly CelulaLookup[]>([]);
  const [busca, setBusca] = useState('');
  const [falhou, setFalhou] = useState(false);
  const [limitado, setLimitado] = useState(false);
  const conhecidas = useRef(new Map<string, CelulaLookup>());

  useEffect(() => {
    let vivo = true;
    const termo = busca.trim();
    const timer = window.setTimeout(
      () => {
        const query = termo === '' ? '' : `?busca=${encodeURIComponent(termo)}`;
        const buscar = (url: string): Promise<CelulaLookup[]> =>
          fetch(url, { credentials: 'include' }).then((res) => {
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return res.json() as Promise<CelulaLookup[]>;
          });
        const urlPrincipal = `/api/v1/estoque/lookups/celulas${query}`;
        const urlDaSelecao = termo === '' && value !== '' && codigoSelecionado
          ? `/api/v1/estoque/lookups/celulas?busca=${encodeURIComponent(codigoSelecionado)}`
          : null;
        Promise.all([buscar(urlPrincipal), urlDaSelecao ? buscar(urlDaSelecao) : Promise.resolve([])])
          .then(([listaPrincipal, listaDaSelecao]) => {
            if (!vivo) return;
            const porId = new Map<string, CelulaLookup>();
            for (const celula of [...listaPrincipal, ...listaDaSelecao]) {
              porId.set(celula.id, celula);
              conhecidas.current.set(celula.id, celula);
            }
            setCelulas([...porId.values()]);
            setFalhou(false);
            if (termo === '' && listaPrincipal.length >= LIMITE_LOOKUP) setLimitado(true);
          })
          .catch(() => {
            if (vivo) setFalhou(true);
          });
      },
      termo === '' ? 0 : ATRASO_BUSCA_MS,
    );
    return () => {
      vivo = false;
      window.clearTimeout(timer);
    };
  }, [busca, codigoSelecionado, value]);

  // A célula escolhida continua visível mesmo quando a busca atual não a traz.
  const escolhida = value === '' ? undefined : conhecidas.current.get(value);
  const opcoes = escolhida && !celulas.some((celula) => celula.id === escolhida.id) ? [escolhida, ...celulas] : celulas;

  return (
    <div className="flex flex-col gap-1">
      {limitado && (
        <Input
          type="search"
          aria-label="Buscar setor / célula"
          placeholder="Buscar por código ou nome da célula"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          disabled={desabilitada}
        />
      )}
      <Select value={value} onValueChange={onChange} disabled={desabilitada}>
        <SelectTrigger id={id} aria-label="Setor / célula" aria-invalid={invalido || undefined} className={className ?? 'w-full'}>
          <SelectValue placeholder={falhou ? 'Não foi possível carregar' : 'Selecione o setor / célula'} />
        </SelectTrigger>
        <SelectContent>
          {opcoes.map((celula) => (
            <SelectItem key={celula.id} value={celula.id}>
              {rotulo(celula)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {falhou && (
        <p className="text-xs text-rose-700" role="alert">
          Não foi possível carregar as células.
        </p>
      )}
    </div>
  );
}
